import React, { useState, useCallback } from 'react';
import { TeachersHeader } from '@/components/teachers/TeachersHeader';
import { TeachersFilter } from '@/components/teachers/TeachersFilter';
import TeacherCard from '@/components/teachers/TeacherCard';
import { TeachersLoading } from '@/components/teachers/TeachersLoading';
import { EmptyTeachersList } from '@/components/teachers/EmptyTeachersList';
import { TeachersPagination } from '@/components/teachers/TeachersPagination';
import { useTeacherData } from '@/hooks/useTeacherData';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { toast } from 'sonner';
import { printTeachersListPages, TeacherPrintData } from '@/utils/printing/teacher';
import { TeacherOfflineIndicator } from '@/components/teachers/TeacherOfflineIndicator';
import { PageSelectionDialog } from '@/components/teachers/PageSelectionDialog';
import TeacherFormatDialog from '@/components/teachers/TeacherFormatDialog';
import { Helmet } from 'react-helmet-async';

const TeachersList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showPageSelection, setShowPageSelection] = useState(false);
  const [showFormatDialog, setShowFormatDialog] = useState(false);
  const [selectedPages, setSelectedPages] = useState<number[]>([]);
  const ITEMS_PER_PAGE = 9; // Define number of items per page for UI display
  const TEACHERS_PER_PRINT_PAGE = 12; // Teachers per print page as requested
  
  const { 
    teachers, 
    loading, 
    error,
    filterText, 
    setFilterText, 
    departmentFilter, 
    setDepartmentFilter, 
    refreshTeachers 
  } = useTeacherData();

  // Calculate pagination
  const totalPages = Math.ceil((teachers?.length || 0) / ITEMS_PER_PAGE);
  const paginatedTeachers = Array.isArray(teachers) 
    ? teachers.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
      )
    : [];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleFilterChange = (newFilter: string) => {
    setDepartmentFilter(newFilter);
    setCurrentPage(1);
  };

  const handleSearch = (query: string) => {
    setFilterText(query);
    setCurrentPage(1);
  };

  const handleDownloadClick = () => {
    if (!teachers || teachers.length === 0) {
      toast.error('No teachers to download');
      return;
    }

    const totalPrintPages = Math.ceil(teachers.length / TEACHERS_PER_PRINT_PAGE);

    // If only one page, show format dialog directly
    if (totalPrintPages === 1) {
      setSelectedPages([]);
      setShowFormatDialog(true);
    } else {
      // Show page selection dialog
      setShowPageSelection(true);
    }
  };

  const handlePageSelection = (selectedPageNumbers?: number[]) => {
    if (selectedPageNumbers) {
      setSelectedPages(selectedPageNumbers);
    } else {
      setSelectedPages([]);
    }
    setShowPageSelection(false);
    setShowFormatDialog(true);
  };

  const handleFormatDownload = async (format: 'jpg' | 'pdf') => {
    if (teachers && teachers.length > 0) {
      try {
        setIsDownloading(true);
        const { printTeachersListAsJPG, printTeachersListAsPDF } = await import('@/utils/printing/teacher');
        
        const teachersList: TeacherPrintData[] = teachers.map(teacher => ({
          id: teacher.id || '',
          name: `${teacher.firstName || ''} ${teacher.lastName || ''}`.trim(),
          department: teacher.department || 'N/A',
          position: teacher.position || 'N/A',
          email: teacher.email || 'N/A',
          phone: teacher.phone || 'N/A',
          qualifications: Array.isArray(teacher.qualifications) 
            ? teacher.qualifications.map(q => typeof q === 'string' ? q : (q?.degree || ''))
            : [],
          joinDate: teacher.joinDate ? new Date(teacher.joinDate).toLocaleDateString() : 'N/A',
          status: teacher.employmentType || 'Active'
        }));
        
        if (format === 'jpg') {
          await printTeachersListAsJPG(teachersList, selectedPages.length > 0 ? selectedPages : undefined);
        } else {
          await printTeachersListAsPDF(teachersList, selectedPages.length > 0 ? selectedPages : undefined);
        }
      } catch (error) {
        console.error('Error downloading teachers list:', error);
        toast.error('Failed to download teachers list');
      } finally {
        setIsDownloading(false);
      }
    } else {
      toast.error('No teachers to download');
    }
  };

  const handleRefresh = useCallback(async () => {
    try {
      await refreshTeachers();
      toast.success('Teacher list refreshed successfully');
    } catch (error) {
      console.error('Error refreshing teachers:', error);
      toast.error('Failed to refresh teacher list');
    }
  }, [refreshTeachers]);

  const teachersPerPage = 20;
  const totalPrintPages = Math.ceil((teachers?.length || 0) / TEACHERS_PER_PRINT_PAGE);

  if (error) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <p className="text-red-500 mb-4">Error loading teachers: {error.message}</p>
          <button 
            className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90"
            onClick={handleRefresh}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <Helmet>
        <title>Teachers List - NALC Student Program & Fee Tracker</title>
      </Helmet>
      
      <TeacherOfflineIndicator resource="list" />
      
      <TeachersHeader onSearch={handleSearch} />
      
      <div className="flex justify-between items-center mb-4">
        <TeachersFilter
          currentFilter={departmentFilter}
          onFilterChange={handleFilterChange}
        />
        
        <Button 
          variant="outline" 
          onClick={handleDownloadClick}
          disabled={isDownloading}
          className="md:hidden flex items-center gap-2"
          size="sm"
        >
          <Download className="h-4 w-4" />
          {isDownloading ? 'Downloading...' : 'Download'}
        </Button>
      </div>
      
      <div className="mt-6">
        {loading ? (
          <TeachersLoading />
        ) : !Array.isArray(teachers) || teachers.length === 0 ? (
          <EmptyTeachersList />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginatedTeachers.map((teacher) => (
              <TeacherCard 
                key={teacher.id} 
                teacher={{
                  id: teacher.id,
                  name: `${teacher.firstName || ''} ${teacher.lastName || ''}`.trim(),
                  email: teacher.email || '',
                  phone: teacher.phone || '',
                  profileImage: teacher.profileImage || '',
                  qualifications: Array.isArray(teacher.qualifications) 
                    ? teacher.qualifications.map(q => typeof q === 'string' ? q : (q?.degree || ''))
                    : [],
                  department: teacher.department || '',
                  bio: teacher.position || ''
                }}
                onDeleteSuccess={refreshTeachers}
              />
            ))}
          </div>
        )}
      </div>
      
      {!loading && Array.isArray(teachers) && teachers.length > 0 && (
        <TeachersPagination 
          currentPage={currentPage} 
          totalPages={totalPages}
          onPageChange={handlePageChange} 
        />
      )}

      <PageSelectionDialog
        isOpen={showPageSelection}
        onClose={() => setShowPageSelection(false)}
        totalPages={totalPrintPages}
        totalTeachers={teachers?.length || 0}
        onDownload={handlePageSelection}
      />

      <TeacherFormatDialog
        isOpen={showFormatDialog}
        onClose={() => setShowFormatDialog(false)}
        onDownload={handleFormatDownload}
        downloadType="list"
      />
    </div>
  );
};

export default TeachersList;
